import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { images } from '../../../assets/images'
import { useNightMode } from '../../../contexts/NightModeContext'

type SideNavMenuProps = {
  minifyNav: boolean;
  mobileNavOpen: boolean
  setMobileNavBarOpen: (value: boolean) => void;
  profileName?: string;
  profileImage?: string;
}

/**
 * Sidebar matching Downloads/eventox demo:
 * brand logo + nav + App Theme + Logout + profile.
 * Theme lives here (not in the top header).
 */
const SideNavMenu = (props: SideNavMenuProps) => {
  const {
    minifyNav,
    mobileNavOpen,
    setMobileNavBarOpen,
    profileName = 'Venue',
    profileImage,
  } = props
  const location = useLocation()
  const { isNightMode, toggleNightMode } = useNightMode()
  const [isHovered, setIsHovered] = useState(false)

  const isOrganizerRoute = location.pathname.includes('/organizer/')
  const expanded = !minifyNav && isHovered

  const onClickLogout = () => {
    localStorage.removeItem('token')
    localStorage.clear()
    window.location.href = '/login'
  }

  const closeMobile = () => setMobileNavBarOpen(false)
  const isActive = (path: string) => location.pathname === path

  const initials = profileName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'V'

  return (
    <>
      {/* Dim + blur the page so the drawer edge is obvious on dark backgrounds */}
      {mobileNavOpen && (
        <button
          type="button"
          className="ex-sidebar-backdrop"
          aria-label="Close menu"
          onClick={closeMobile}
        />
      )}
      <aside
      id="sidebar-container"
      className={`ex-sidebar ${expanded ? 'is-expanded' : ''} ${mobileNavOpen ? 'is-mobile-open' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Brand — same as Downloads mock sidebar */}
      <div className="ex-sidebar-logo">
        <img
          src={isNightMode ? images.eventox_logo_dark : images.eventox_light}
          alt="Eventox"
          className="logo-full"
        />
        <img
          src={isNightMode ? images.white_icon : images.grey_logo}
          alt="Eventox"
          className="logo-icon"
        />
      </div>

      <nav className="ex-sidebar-nav" id="js-menu">
        {isOrganizerRoute ? (
          <Link
            to="/home"
            className={isActive('/home') ? 'active' : ''}
            title="Go Back"
            onClick={closeMobile}
          >
            <i className="fa-solid fa-arrow-left nav-icon"></i>
            <span className="nav-label">Go Back</span>
          </Link>
        ) : (
          <>
            <Link to="/home" className={isActive('/home') ? 'active' : ''} title="Organizers" onClick={closeMobile}>
              <i className="fa-solid fa-calendar-days nav-icon"></i>
              <span className="nav-label">Organizers</span>
            </Link>
            <Link to="/update-venue" className={isActive('/update-venue') ? 'active' : ''} title="Update Venue" onClick={closeMobile}>
              <i className="fa-solid fa-location-dot nav-icon"></i>
              <span className="nav-label">Update Venue</span>
            </Link>
            <Link to="/create-organizer" className={isActive('/create-organizer') ? 'active' : ''} title="Create Organizer" onClick={closeMobile}>
              <i className="fa-solid fa-square-plus nav-icon"></i>
              <span className="nav-label">Create Organizer</span>
            </Link>
            <Link to="/events-schedule" className={isActive('/events-schedule') ? 'active' : ''} title="Events Schedule" onClick={closeMobile}>
              <i className="fa-solid fa-calendar-week nav-icon"></i>
              <span className="nav-label">Events Schedule</span>
            </Link>
            <Link to="/venue-events" className={isActive('/venue-events') ? 'active' : ''} title="Venue Events" onClick={closeMobile}>
              <i className="fa-solid fa-calendar-days nav-icon"></i>
              <span className="nav-label">Venue Events</span>
            </Link>
          </>
        )}
      </nav>

      <div className="ex-sidebar-bottom">
        {/* App Theme — mock sidebar control */}
        <button
          type="button"
          className="ex-nav-item"
          onClick={toggleNightMode}
          title="Switch App Theme"
        >
          <i
            className="fa-solid fa-palette nav-icon"
            style={{ color: isNightMode ? '#EE3F7C' : '#64748b' }}
          ></i>
          <span className="nav-label-stack">
            <span className="nav-label-eyebrow">App Theme</span>
            <span className="nav-label-title">
              {isNightMode ? 'Eventox Original' : 'Classic Gray'}
            </span>
          </span>
        </button>

        <button
          type="button"
          className="ex-nav-item ex-nav-logout"
          onClick={onClickLogout}
          title="Logout"
        >
          <i className="fa-solid fa-right-from-bracket nav-icon"></i>
          <span className="nav-label">Logout</span>
        </button>

        <div className="ex-sidebar-profile">
          {profileImage ? (
            <img src={profileImage} alt="" className="avatar" />
          ) : (
            <div className="avatar">{initials}</div>
          )}
          <div className="profile-meta">
            <span className="name">{profileName}</span>
            <span className="role">Venue</span>
          </div>
        </div>
      </div>
    </aside>
    </>
  )
}

export default SideNavMenu
